/**
 * SF-5127 — when an email last came in for the company (`help_desk_last_inbound_at`), shown under
 * the address customers' emails arrive at, so staff can tell the way in is being used. Not having
 * had an email yet is not proof anything is wrong, so it never says the address is broken; it
 * suggests a test email instead.
 */
import { Tooltip, Typography } from 'antd';

const { Text } = Typography;

const UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ['year', 365 * 24 * 3600],
  ['month', 30 * 24 * 3600],
  ['day', 24 * 3600],
  ['hour', 3600],
  ['minute', 60],
];
const relative = new Intl.RelativeTimeFormat('en', { numeric: 'always' });

/** "5 minutes ago", "3 days ago"; under a minute is "just now". */
function timeAgo(at: Date, now: number = Date.now()): string {
  const seconds = Math.max(0, (now - at.getTime()) / 1000);
  for (const [unit, size] of UNITS) {
    if (seconds >= size) return relative.format(-Math.floor(seconds / size), unit);
  }
  return 'just now';
}

export const HelpDeskLastEmail: React.FC<{
  at: string | null | undefined;
  /** Where a test email goes: the address customers write to. */
  testAddress: string;
}> = ({ at, testAddress }) => {
  const when = at ? new Date(at) : null;
  return (
    <div data-testid="help-desk-last-email">
      {when && !Number.isNaN(when.getTime()) ? (
        <Text>
          Last email received{' '}
          <Tooltip title={when.toLocaleString()}>
            <span>{timeAgo(when)}</span>
          </Tooltip>
        </Text>
      ) : (
        <Text type="secondary">
          {`No email received yet. Send a test email to ${testAddress} to check it works.`}
        </Text>
      )}
    </div>
  );
};
